import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { auth } from '../firebase';
import { signInWithEmailAndPassword, onAuthStateChanged, signOut, sendPasswordResetEmail } from 'firebase/auth';

const AffiliateDashboard = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dashData, setDashData] = useState(null);
  const [dashLoading, setDashLoading] = useState(false);
  const [dashError, setDashError] = useState(null);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState(null);
  const [loginLoading, setLoginLoading] = useState(false);
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setLoading(false);
    });
    return unsub;
  }, []);

  const loadDashboard = useCallback(async () => {
    if (!user) return;
    setDashLoading(true);
    setDashError(null);
    try {
      const idToken = await user.getIdToken();
      const res = await fetch('/.netlify/functions/get-affiliate-data', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ idToken }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setDashData(data);
    } catch (err) {
      setDashError(err.message || 'Failed to load dashboard');
    } finally {
      setDashLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      loadDashboard();
    }
  }, [user, loadDashboard]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError(null);
    try {
      await signInWithEmailAndPassword(auth, loginEmail, loginPassword);
    } catch (err) {
      if (err.code === 'auth/invalid-credential' || err.code === 'auth/wrong-password') {
        setLoginError('Invalid email or password.');
      } else if (err.code === 'auth/user-not-found') {
        setLoginError('No affiliate account found with this email.');
      } else {
        setLoginError('Login failed. Please try again.');
      }
    } finally {
      setLoginLoading(false);
    }
  };

  const [resetSent, setResetSent] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  const handleForgotPassword = async () => {
    if (!loginEmail) {
      setLoginError('Enter your email address above, then click Forgot Password.');
      return;
    }
    setResetLoading(true);
    setLoginError(null);
    try {
      await sendPasswordResetEmail(auth, loginEmail);
      setResetSent(true);
    } catch (err) {
      if (err.code === 'auth/user-not-found') {
        setLoginError('No affiliate account found with this email.');
      } else {
        setLoginError('Unable to send reset email. Please try again.');
      }
    } finally {
      setResetLoading(false);
    }
  };

  const handleLogout = async () => {
    await signOut(auth);
    setDashData(null);
  };

  const [connectLoading, setConnectLoading] = useState(false);
  const handleConnectSetup = async () => {
    setConnectLoading(true);
    try {
      const idToken = await user.getIdToken();
      const res = await fetch('/.netlify/functions/create-connect-account', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ idToken }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      window.location.href = data.url;
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setConnectLoading(false);
    }
  };

  const [dashboardLinkLoading, setDashboardLinkLoading] = useState(false);
  const handleManageAccount = async () => {
    setDashboardLinkLoading(true);
    try {
      const idToken = await user.getIdToken();
      const res = await fetch('/.netlify/functions/connect-dashboard', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ idToken }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      window.open(data.url, '_blank');
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setDashboardLinkLoading(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(null), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-32 pb-20 px-6 flex items-center justify-center">
        <div className="text-muted text-sm">Loading...</div>
      </div>
    );
  }

  // Login screen
  if (!user) {
    return (
      <div className="min-h-screen pt-32 pb-20 px-6">
        <div className="max-w-md mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="font-mono text-[0.7rem] tracking-[0.25em] uppercase text-teal mb-4 text-center">
              Affiliate Dashboard
            </div>
            <h1 className="font-display text-[clamp(2rem,4vw,3rem)] tracking-[0.04em] leading-none text-white mb-8 text-center">
              Log in to your account.
            </h1>

            <form onSubmit={handleLogin} className="space-y-5">
              {loginError && (
                <div className="bg-red-500/10 border border-red-500/20 rounded p-3 text-[0.85rem] text-red-400">
                  {loginError}
                </div>
              )}
              <div>
                <label className="block font-mono text-[0.6rem] tracking-[0.2em] uppercase text-muted mb-1.5">
                  Email
                </label>
                <input
                  type="email"
                  required
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  className="w-full bg-dark3 border border-white/[0.06] rounded px-4 py-3 text-[0.9rem] text-white/90 font-light focus:border-teal/40 focus:outline-none transition-colors"
                  placeholder="Your affiliate email"
                />
              </div>
              <div>
                <label className="block font-mono text-[0.6rem] tracking-[0.2em] uppercase text-muted mb-1.5">
                  Password
                </label>
                <input
                  type="password"
                  required
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  className="w-full bg-dark3 border border-white/[0.06] rounded px-4 py-3 text-[0.9rem] text-white/90 font-light focus:border-teal/40 focus:outline-none transition-colors"
                  placeholder="Your password"
                />
              </div>
              <button
                type="submit"
                disabled={loginLoading}
                className={`w-full bg-teal text-dark font-body text-[0.78rem] font-semibold tracking-[0.08em] uppercase py-3.5 px-6 rounded-sm transition-all ${
                  loginLoading ? 'opacity-60 cursor-wait' : 'hover:opacity-90 hover:-translate-y-[1px]'
                }`}
              >
                {loginLoading ? 'Logging in...' : 'Log In'}
              </button>

              <button
                type="button"
                onClick={handleForgotPassword}
                disabled={resetLoading}
                className="w-full text-[0.78rem] text-muted hover:text-teal transition-colors mt-3"
              >
                {resetLoading ? 'Sending...' : 'Forgot password?'}
              </button>

              {resetSent && (
                <div className="bg-teal/10 border border-teal/20 rounded p-3 text-[0.85rem] text-teal mt-3">
                  Password reset email sent to {loginEmail}. Check your inbox (and spam folder for synemafest@gmail.com).
                </div>
              )}
            </form>

            <p className="text-center mt-6 text-[0.8rem] text-muted">
              Don't have an account?{' '}
              <a href="/affiliates" className="text-teal">Apply to become an affiliate</a>
            </p>
          </motion.div>
        </div>
      </div>
    );
  }

  // Dashboard
  return (
    <div className="min-h-screen pt-32 pb-20 px-6">
      <div className="max-w-[1200px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Header */}
          <div className="flex justify-between items-start mb-12">
            <div>
              <div className="font-mono text-[0.7rem] tracking-[0.25em] uppercase text-teal mb-4">
                Affiliate Dashboard
              </div>
              <h1 className="font-display text-[clamp(2rem,4vw,3rem)] tracking-[0.04em] leading-none text-white">
                {dashData ? `Welcome, ${dashData.name.split(' ')[0]}.` : 'Loading...'}
              </h1>
            </div>
            <button
              onClick={handleLogout}
              className="text-[0.78rem] text-muted hover:text-white transition-colors mt-2"
            >
              Log out
            </button>
          </div>

          {dashLoading && (
            <div className="text-center py-20 text-muted">Loading dashboard data...</div>
          )}

          {dashError && (
            <div className="bg-red-500/10 border border-red-500/20 rounded p-4 mb-8 text-[0.85rem] text-red-400">
              {dashError}
            </div>
          )}

          {dashData && (
            <>
              {/* Tier badge */}
              <div className="mb-8">
                <span className={`inline-block font-mono text-[0.65rem] tracking-[0.2em] uppercase px-3 py-1 rounded-sm ${
                  dashData.tier === 'partner'
                    ? 'bg-orange/10 border border-orange/20 text-orange'
                    : 'bg-teal/10 border border-teal/20 text-teal'
                }`}>
                  {dashData.tier === 'partner' ? 'Partner Affiliate — 15%' : 'Standard Affiliate — 10%'}
                </span>
              </div>

              {/* Stats grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
                {[
                  { label: 'Total Conversions', value: dashData.totalConversions, color: 'text-teal' },
                  { label: 'Total Commission', value: `$${(dashData.totalCommission / 100).toFixed(2)}`, color: 'text-gold' },
                  { label: 'Total Paid', value: `$${(dashData.totalPaid / 100).toFixed(2)}`, color: 'text-teal' },
                  { label: 'Balance Owed', value: `$${((dashData.totalCommission - dashData.totalPaid) / 100).toFixed(2)}`, color: 'text-orange' },
                ].map((stat) => (
                  <div key={stat.label} className="bg-dark2 border border-white/[0.06] rounded p-6">
                    <div className={`font-display text-3xl ${stat.color}`}>{stat.value}</div>
                    <div className="font-mono text-[0.6rem] tracking-[0.15em] uppercase text-muted mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>

              {/* Referral link & promo code */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
                <div className="bg-dark2 border border-teal/20 rounded p-6">
                  <h3 className="font-display text-lg text-white mb-3">YOUR REFERRAL LINK</h3>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      readOnly
                      value={dashData.referralLink}
                      className="flex-1 bg-dark3 border border-white/[0.06] rounded px-3 py-2 text-[0.8rem] text-teal font-mono"
                    />
                    <button
                      onClick={() => copyToClipboard(dashData.referralLink, 'link')}
                      className="bg-teal text-dark px-4 py-2 rounded-sm text-[0.75rem] font-semibold uppercase tracking-wider hover:opacity-90 transition-all"
                    >
                      {copied === 'link' ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                  <p className="text-[0.72rem] text-muted mt-2">
                    Share this link — anyone who purchases through it is attributed to you.
                  </p>
                </div>

                <div className="bg-dark2 border border-gold/20 rounded p-6">
                  <h3 className="font-display text-lg text-white mb-3">YOUR PROMO CODE</h3>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      readOnly
                      value={dashData.promoCode}
                      className="flex-1 bg-dark3 border border-white/[0.06] rounded px-3 py-2 text-[0.8rem] text-gold font-mono tracking-widest text-center"
                    />
                    <button
                      onClick={() => copyToClipboard(dashData.promoCode, 'code')}
                      className="bg-gold text-dark px-4 py-2 rounded-sm text-[0.75rem] font-semibold uppercase tracking-wider hover:opacity-90 transition-all"
                    >
                      {copied === 'code' ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                  <p className="text-[0.72rem] text-muted mt-2">
                    {dashData.tier === 'partner' ? '10% off for your audience — you earn up to 15%' : '10% off for your audience — you earn up to 10%'}
                  </p>
                </div>
              </div>

              {/* Promo Materials */}
              <div className="bg-dark2 border border-white/[0.06] rounded p-6 mb-12">
                <h3 className="font-display text-lg text-white mb-4">PROMO MATERIALS</h3>
                <p className="text-[0.88rem] font-light text-white/50 mb-4">
                  Download assets to share with your audience. Right-click or long-press to save.
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { name: 'Festival Logo', desc: 'PNG, transparent bg', file: '/Synema Logo.png' },
                    { name: 'Social Square', desc: 'Square format for posts', file: '/Synema Fest Sq.png' },
                    { name: 'Story Template', desc: 'Vertical for Stories/Reels', file: '/Story Template Vertical .png' },
                    { name: 'Banner', desc: 'Wide format for sharing', file: '/Synema Fest Banner.png' },
                  ].map((asset) => (
                    <a
                      key={asset.name}
                      href={asset.file}
                      download
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-dark3 border border-white/[0.06] rounded p-4 text-center hover:border-teal/30 transition-colors group no-underline"
                    >
                      <div className="w-full aspect-video bg-dark rounded overflow-hidden mb-3 border border-white/[0.04]">
                        <img src={asset.file} alt={asset.name} className="w-full h-full object-contain" />
                      </div>
                      <div className="text-[0.8rem] text-white/70 font-medium group-hover:text-teal transition-colors">{asset.name}</div>
                      <div className="text-[0.65rem] text-muted">{asset.desc}</div>
                    </a>
                  ))}
                </div>
              </div>

              {/* Recent conversions */}
              <div className="bg-dark2 border border-white/[0.06] rounded p-6 mb-12">
                <h3 className="font-display text-lg text-white mb-4">RECENT CONVERSIONS</h3>
                {dashData.commissions.length === 0 ? (
                  <p className="text-[0.88rem] font-light text-white/40 text-center py-8">
                    No conversions yet. Share your referral link to start earning!
                  </p>
                ) : (
                  <div className="space-y-2">
                    {dashData.commissions.map((c) => (
                      <div key={c.id} className="flex justify-between items-center py-3 border-b border-white/[0.04] last:border-0">
                        <div>
                          <div className="text-[0.85rem] text-white/70">
                            Ticket sale — ${(c.amount / 100).toFixed(2)}
                          </div>
                          <div className="text-[0.7rem] text-muted">
                            {c.createdAt ? new Date(c.createdAt).toLocaleDateString() : 'Recent'}
                          </div>
                        </div>
                        <div className="font-mono text-[0.85rem] text-gold">
                          +${(c.commission / 100).toFixed(2)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Payout info */}
              <div className="bg-dark2 border border-white/[0.06] rounded p-6">
                <h3 className="font-display text-lg text-white mb-4">PAYOUTS</h3>

                {dashData.connectStatus === 'none' || dashData.connectStatus === 'pending' ? (
                  <div>
                    <p className="text-[0.88rem] font-light text-white/50 leading-relaxed mb-4">
                      {dashData.connectStatus === 'pending'
                        ? 'Your Stripe Connect account has been created but setup is not complete. Finish onboarding to add your bank details and start receiving payouts.'
                        : 'To receive payouts, you need to set up your Stripe Connect account. This lets us send commission payments directly to your bank account.'}
                    </p>
                    <div className="bg-dark3 border border-white/[0.06] rounded p-4 mb-5">
                      <div className="font-mono text-[0.6rem] tracking-[0.2em] uppercase text-teal mb-2">Quick setup tips</div>
                      <ul className="space-y-1.5 text-[0.8rem] font-light text-white/50">
                        <li>Business type: Select <strong className="text-white/70">"Individual"</strong> if you don't have an LLC</li>
                        <li>Legal name: Use your full legal name as it appears on your ID</li>
                        <li>Website: Click <strong className="text-white/70">"Don't have a website? Add product description instead"</strong> and enter <strong className="text-white/70">"Affiliate Ticket Sales"</strong></li>
                        <li>You'll need your bank account or debit card details for payouts</li>
                      </ul>
                    </div>
                    <button
                      onClick={handleConnectSetup}
                      disabled={connectLoading}
                      className={`bg-teal text-dark font-body text-[0.78rem] font-semibold tracking-[0.08em] uppercase py-3 px-6 rounded-sm transition-all ${
                        connectLoading ? 'opacity-60 cursor-wait' : 'hover:opacity-90 hover:-translate-y-[1px]'
                      }`}
                    >
                      {connectLoading ? 'Redirecting to Stripe...' : dashData.connectStatus === 'pending' ? 'Complete Stripe Setup' : 'Set Up Stripe Connect'}
                    </button>
                  </div>
                ) : (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-teal" />
                        <span className="text-[0.8rem] text-teal">Stripe Connect active — payouts enabled</span>
                      </div>
                      <button
                        onClick={handleManageAccount}
                        disabled={dashboardLinkLoading}
                        className="border border-white/[0.1] text-white/60 hover:text-white hover:border-white/20 px-4 py-1.5 rounded-sm text-[0.72rem] font-medium tracking-wider uppercase transition-all"
                      >
                        {dashboardLinkLoading ? 'Loading...' : 'Manage Payout Account'}
                      </button>
                    </div>
                    <p className="text-[0.85rem] font-light text-white/50 leading-relaxed mb-4">
                      Commissions are paid out by the Synema Film Fest team. You'll receive an email and see it reflected here when a payout is sent. Use "Manage Payout Account" to update your bank details or view payout history.
                    </p>
                  </div>
                )}

                <div className="mt-4 grid grid-cols-2 gap-4">
                  <div className="bg-dark3 rounded p-4">
                    <div className="text-[0.7rem] font-mono tracking-wider uppercase text-muted mb-1">Total earned</div>
                    <div className="font-display text-2xl text-gold">${(dashData.totalCommission / 100).toFixed(2)}</div>
                  </div>
                  <div className="bg-dark3 rounded p-4">
                    <div className="text-[0.7rem] font-mono tracking-wider uppercase text-muted mb-1">Pending payout</div>
                    <div className="font-display text-2xl text-orange">${((dashData.totalCommission - dashData.totalPaid) / 100).toFixed(2)}</div>
                  </div>
                </div>
                <p className="text-[0.72rem] text-muted mt-3">Minimum payout: $25.00. Commissions accrue until the threshold is reached.</p>
              </div>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default AffiliateDashboard;
